import { defineStore } from 'pinia';
import { useApi } from '@lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import type { SkuCodeRangeType, SkuCodeType } from '@/ApiClient';
import { assertUnreachable } from '@lib/type-helpers';

export const useSkuCodeRanges = defineStore('sku-code-ranges', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.skuCodeRange_GetRanges,
    dto: 'SkuCodeNumericRangeDto',
    create: api.skuCodeRange_CreateRange,
    update: api.skuCodeRange_UpdateRange,
    delete: api.skuCodeRange_DeleteRange,
    signalR: 'SkuCodeUpdate',
  });

  return {
    ...globalStoreBase,
  };
});

// Accepts both the field type (which can be Mixed) and the range type (Ean/Upc only).
export function isValidSkuCode(type: SkuCodeType | SkuCodeRangeType, code: string | null) {
  if (type === 'Ean') return isValidEan(code);
  if (type === 'Upc') return isValidUpc(code);
  if (type === 'Mixed') return isValidEan(code) || isValidUpc(code);

  return assertUnreachable(type);
}

// A Mixed field can hold either an EAN or a UPC value; resolve which concrete
// format a given value uses so it can be displayed/rendered correctly.
function resolveMixedValueType(code: string): 'Ean' | 'Upc' {
  if (isValidUpc(code)) return 'Upc';
  if (isValidEan(code)) return 'Ean';

  return code.length === 12 ? 'Upc' : 'Ean';
}

// See https://en.wikipedia.org/wiki/ISBN#ISBN-13_check_digit_calculation for clear explanation of checksum
function isValidEan(code: string | null) {
  if (code?.length !== 13) return false;

  let sum = 0;
  let triple = false;

  for (const c of code) {
    sum += parseInt(c) * (triple ? 3 : 1);
    triple = !triple;
  }

  return sum % 10 === 0;
}

function isValidUpc(code: string | null) {
  if (code?.length !== 12) return false;

  return isValidEan('0' + code);
}

export function skuCodeValueImagePath(type: SkuCodeType, skuCodeValue: string) {
  const resolved = type === 'Mixed' ? resolveMixedValueType(skuCodeValue) : type;

  if (resolved === 'Ean') return `/api/sku-codes/ean-images/${skuCodeValue}.png`;
  if (resolved === 'Upc') return `/api/sku-codes/upc-images/${skuCodeValue}.png`;

  return assertUnreachable(resolved);
}
